// Generic, source-agnostic Calendar view (ADR D3/P0.5).
//
// Month grid keyed by record.dueDate. Source-agnostic via ResolverContext.
// `month` (YYYY-MM) is controllable for deterministic tests; defaults to the
// current month. Filters from ViewState apply; grouping/sort are ignored (a
// calendar's "grouping" is the date itself).
//
// #876 drag & drop — chosen semantics (see computeDropChange below for the
// pure date math): only records with a dueDate are ever rendered here (an
// undated record has nowhere on the grid to go), so a chip always represents
// at least that one date. Dropping it onto another day:
// - No startDate (or startDate === dueDate): a single-date chip — set
//   dueDate to the target day.
// - startDate present and different from dueDate (a ranged record, even
//   though this grid only visualizes the due date): shift BOTH startDate and
//   dueDate by the same day-delta, preserving the range's duration instead
//   of collapsing it onto the target day.
// A drop back onto the same day is a no-op. Read-only viewers get no drag
// affordance at all.

import { useMemo, useState } from "preact/hooks";
import { ChevronLeft, ChevronRight } from "lucide-preact";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import type { DropResult } from "@hello-pangea/dnd";
import { addDaysIso, diffDaysIso, localTodayIso as localToday, softPillStyle } from "../lib/format";
import type { ResolverContext, TaskRecord, TaskRecordChange } from "../lib/taskRecord";
import { applyFilters, type ViewState } from "../lib/viewState";

interface Props {
  records: TaskRecord[];
  state: ViewState;
  ctx: ResolverContext;
  /** Initial month as YYYY-MM. Defaults to the current month. */
  month?: string;
  onOpenRecord?: (record: TaskRecord) => void;
  /** Injectable "today" (YYYY-MM-DD) for filters + today-highlight in tests. */
  today?: string;
  /** Viewer has no write access — disables all drag affordance. */
  readOnly?: boolean;
}

/**
 * Calendar-chip drop: see the semantics comment block at the top of the
 * file. `dueDate` is guaranteed non-null here — only dated records are ever
 * rendered as draggable chips. Returns null (no-op) when dropped back on the
 * same day.
 */
export function computeDropChange(
  record: Pick<TaskRecord, "startDate" | "dueDate">,
  targetIso: string
): TaskRecordChange | null {
  const dueDate = record.dueDate;
  if (!dueDate || dueDate === targetIso) return null;
  if (record.startDate && record.startDate !== dueDate) {
    const delta = diffDaysIso(dueDate, targetIso);
    return { startDate: addDaysIso(record.startDate, delta), dueDate: targetIso };
  }
  return { dueDate: targetIso };
}

const WEEKDAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

function currentMonth(): string {
  return localToday().slice(0, 7);
}

/** Build the 6×7 day grid (YYYY-MM-DD strings) covering the month, Monday-first. */
function monthGrid(month: string): string[] {
  const [y, m] = month.split("-").map(Number);
  const first = new Date(Date.UTC(y, m - 1, 1));
  const startDow = (first.getUTCDay() + 6) % 7; // 0 = Monday
  const start = new Date(first);
  start.setUTCDate(first.getUTCDate() - startDow);
  const days: string[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setUTCDate(start.getUTCDate() + i);
    days.push(d.toISOString().slice(0, 10));
  }
  return days;
}

function shiftMonth(month: string, delta: number): string {
  const [y, m] = month.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return d.toISOString().slice(0, 7);
}

function monthLabel(month: string): string {
  const [y, m] = month.split("-").map(Number);
  const names = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];
  return `${names[m - 1]} ${y}`;
}

export function TaskCalendarView(props: Props): preact.JSX.Element {
  const { records, state, ctx, today, readOnly = false } = props;
  const [month, setMonth] = useState(props.month ?? currentMonth());

  const filtered = applyFilters(records, state.filters, { today });
  const byDay = new Map<string, TaskRecord[]>();
  for (const r of filtered) {
    if (!r.dueDate) continue;
    if (!byDay.has(r.dueDate)) byDay.set(r.dueDate, []);
    byDay.get(r.dueDate)!.push(r);
  }

  // uid → record lookup so onDragEnd can resolve the dragged chip (same
  // pattern as TaskBoardView.tsx).
  const byUid = useMemo(() => {
    const m = new Map<string, TaskRecord>();
    for (const r of filtered) m.set(r.uid, r);
    return m;
  }, [filtered]);

  const days = monthGrid(month);
  const todayIso = today ?? localToday();
  const undated = filtered.filter((r) => !r.dueDate).length;

  // See the semantics comment block at the top of the file. Every day cell
  // is a Droppable with id "day:<iso>".
  const onDragEnd = (result: DropResult): void => {
    if (readOnly) return;
    const { destination, draggableId } = result;
    if (!destination || !destination.droppableId.startsWith("day:")) return;
    const targetIso = destination.droppableId.slice("day:".length);
    const record = byUid.get(draggableId);
    if (!record) return;
    const change = computeDropChange(record, targetIso);
    if (!change) return;
    void ctx.patch(record, change);
  };

  return (
    <div class="flex flex-col gap-2">
      <div class="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setMonth((mm) => shiftMonth(mm, -1))}
          class="inline-flex items-center justify-center size-7 rounded border border-[color:var(--color-outline-variant)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
          aria-label="Vorheriger Monat"
        >
          <ChevronLeft size={16} aria-hidden="true" />
        </button>
        <span class="text-sm font-semibold text-[color:var(--color-on-surface)] min-w-[140px] text-center">{monthLabel(month)}</span>
        <button
          type="button"
          onClick={() => setMonth((mm) => shiftMonth(mm, 1))}
          class="inline-flex items-center justify-center size-7 rounded border border-[color:var(--color-outline-variant)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
          aria-label="Nächster Monat"
        >
          <ChevronRight size={16} aria-hidden="true" />
        </button>
        {undated > 0 && (
          <span class="ml-2 text-label text-[color:var(--color-on-surface-variant)]">{undated} ohne Datum</span>
        )}
      </div>

      <DragDropContext onDragEnd={onDragEnd}>
        <div class="grid grid-cols-7 gap-px rounded overflow-hidden border border-[color:var(--color-outline-variant)] bg-[color:var(--color-outline-variant)]">
          {WEEKDAYS.map((w) => (
            <div key={w} class="bg-[color:var(--color-surface-container)] px-2 py-1 text-label font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)] text-center">
              {w}
            </div>
          ))}
          {days.map((day) => {
            const inMonth = day.slice(0, 7) === month;
            const items = byDay.get(day) ?? [];
            const isToday = day === todayIso;
            return (
              <Droppable key={day} droppableId={`day:${day}`} type="CAL_RECORD" isDropDisabled={readOnly}>
                {(prov, snap) => (
                  <div
                    ref={prov.innerRef}
                    {...prov.droppableProps}
                    class={`min-h-[84px] p-1 flex flex-col gap-0.5 transition-colors ${
                      snap.isDraggingOver
                        ? "ring-2 ring-inset ring-[color:var(--color-primary)] bg-[color:var(--color-surface-container-high)]"
                        : inMonth
                          ? "bg-[color:var(--color-surface-container-lowest)]"
                          : "bg-[color:var(--color-surface-container)]/40"
                    }`}
                  >
                    <span
                      class={`text-label tabular-nums self-end px-1 rounded ${
                        isToday
                          ? "bg-[color:var(--color-primary)] text-[color:var(--color-on-primary)] font-semibold"
                          : inMonth
                            ? "text-[color:var(--color-on-surface-variant)]"
                            : "text-[color:var(--color-on-surface-variant)]/50"
                      }`}
                    >
                      {Number(day.slice(8, 10))}
                    </span>
                    {items.slice(0, 4).map((r, idx) => (
                      <Draggable key={r.uid} draggableId={r.uid} index={idx} isDragDisabled={readOnly}>
                        {(dragProv, dragSnap) => (
                          <button
                            ref={dragProv.innerRef}
                            {...(dragProv.draggableProps as preact.JSX.HTMLAttributes<HTMLButtonElement>)}
                            {...(dragProv.dragHandleProps as preact.JSX.HTMLAttributes<HTMLButtonElement> | undefined)}
                            type="button"
                            onClick={() => props.onOpenRecord?.(r)}
                            class={`flex items-center gap-1 text-left truncate rounded px-1 py-0.5 text-label hover:opacity-80 focus:outline-none focus-visible:ring-1 focus-visible:ring-[color:var(--color-primary)] ${
                              dragSnap.isDragging ? "shadow-level-2" : ""
                            }`}
                            style={{
                              ...((dragProv.draggableProps.style ?? {}) as preact.JSX.CSSProperties),
                              ...softPillStyle(r.status.color),
                            }}
                            title={r.ticket ? `${r.ticket} · ${r.title}` : r.title}
                          >
                            {r.ticket && (
                              <span class="select-text font-mono text-label font-medium opacity-70 shrink-0">
                                {r.ticket}
                              </span>
                            )}
                            <span class={`truncate ${r.done ? "line-through opacity-70" : ""}`}>{r.title}</span>
                          </button>
                        )}
                      </Draggable>
                    ))}
                    {items.length > 4 && (
                      <span class="text-label text-[color:var(--color-on-surface-variant)] px-1">+{items.length - 4} mehr</span>
                    )}
                    {prov.placeholder}
                  </div>
                )}
              </Droppable>
            );
          })}
        </div>
      </DragDropContext>
    </div>
  );
}
